import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";

const SEAL_URL = "https://static.prod-images.emergentagent.com/jobs/4e2bdc98-ab8f-417d-b100-db42a3ecfaf3/images/2c4d4f557900b39c29a30c3708dd6bf80de599af925c4589b0748935140e0e0b.png";

const NAV_ITEMS = [
  { label: "Process",     href: "/#process",     testid: "nav-process" },
  { label: "Case Files",  href: "/blog",         testid: "nav-blog", route: true },
  { label: "Pricing",     href: "/pricing",      testid: "nav-pricing", route: true },
  { label: "Accounting",  href: "/accounting",   testid: "nav-accounting", route: true },
  { label: "Listings",    href: "/listings",     testid: "nav-listings", route: true },
  { label: "FAQ",         href: "/#faq",         testid: "nav-faq" },
  { label: "Contact",     href: "/contact",      testid: "nav-contact", route: true },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  const linkClass =
    "font-mono text-[11px] uppercase tracking-[0.22em] text-stone-700 hover:text-stone-900 transition-colors";

  return (
    <header
      className={`sticky top-0 z-50 bg-white transition-[border-color] duration-300 ${
        scrolled ? "border-b border-stone-200" : "border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-home-link">
          <img src={SEAL_URL} alt="" className="h-8 w-8 object-contain grayscale opacity-90" />
          <div className="leading-none">
            <div className="font-serif text-lg text-stone-900 tracking-tight">Azure Gypsy Guild</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-stone-500 mt-1">
              Private Members Trust · Est. 2026
            </div>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((item) =>
            item.route ? (
              <Link key={item.label} to={item.href} className={linkClass} data-testid={item.testid}>
                {item.label}
              </Link>
            ) : (
              <a key={item.label} href={item.href} className={linkClass} data-testid={item.testid}>
                {item.label}
              </a>
            )
          )}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-4">
          {user ? (
            <>
              <Link
                to={user.role === "admin" ? "/admin" : "/portal"}
                className="btn-ghost"
                data-testid="nav-portal-btn"
              >
                {user.role === "admin" ? "Admin" : "My Portal"}
              </Link>
              <button onClick={handleLogout} className={linkClass} data-testid="nav-logout-btn">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className={linkClass} data-testid="nav-login-btn">
                Sign in
              </Link>
              <Link to="/register" className="btn-primary" data-testid="nav-join-btn">
                Join the Guild
              </Link>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen((v) => !v)}
          className="md:hidden h-10 w-10 border border-stone-300 flex items-center justify-center text-stone-900"
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile panel */}
      {open && (
        <div className="md:hidden border-t border-stone-200 bg-white" data-testid="nav-mobile-panel">
          <div className="px-6 py-8 flex flex-col gap-6">
            {NAV_ITEMS.map((item) =>
              item.route ? (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setOpen(false)}
                  className={linkClass}
                  data-testid={`${item.testid}-mobile`}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className={linkClass}
                  data-testid={`${item.testid}-mobile`}
                >
                  {item.label}
                </a>
              )
            )}
            <div className="hairline mt-2" />
            {user ? (
              <>
                <Link
                  to={user.role === "admin" ? "/admin" : "/portal"}
                  onClick={() => setOpen(false)}
                  className="btn-outline w-full"
                  data-testid="nav-portal-btn-mobile"
                >
                  {user.role === "admin" ? "Admin" : "My Portal"}
                </Link>
                <button onClick={handleLogout} className="btn-ghost self-start" data-testid="nav-logout-btn-mobile">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="btn-outline w-full" data-testid="nav-login-btn-mobile">
                  Sign in
                </Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-primary w-full" data-testid="nav-join-btn-mobile">
                  Join the Guild
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
